import { Component } from '@theme/component';
import { onDocumentReady, changeMetaThemeColor, throttle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/** @type {IntersectionObserver | null} */
let headerTransparencyObserver = null;

/** @type {(() => void) | null} */
let updateHeaderGroupHeightsRef = null;

/** @type {((event: Event) => void) | null} */
let headerTransparencyScrollHandler = null;

/**
 * In-flow height of header-group children above main (announcement bar, etc.).
 * Excludes overlay header-component so full-screen heroes fill the viewport correctly.
 *
 * @param {HTMLElement | null} header
 * @param {HTMLElement | null} headerGroup
 */
function calculateHeaderGroupFlowHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }
  return totalHeight;
}

/**
 * @param {HTMLElement} header
 */
function updateHeaderTransparentState(header) {
  const mode = header.getAttribute('transparent');
  if (mode !== 'transparent-top' && mode !== 'transparent-scroll') return;

  const scrollTop = Math.max(
    window.scrollY || 0,
    document.documentElement.scrollTop || 0,
    document.body.scrollTop || 0
  );
  const nextState = scrollTop > 12 ? 'scrolled' : 'top';

  if (header.dataset.transparentState !== nextState) {
    header.dataset.transparentState = nextState;
    const headerRow = header.querySelector('[ref="headerRowTop"]');
    if (headerRow instanceof HTMLElement) {
      changeMetaThemeColor(headerRow);
    }
    updateHeaderGroupHeightsRef?.();
  }
}

/**
 * @param {HTMLElement} header
 */
function initHeaderTransparency(header) {
  const mode = header.getAttribute('transparent');
  if (mode !== 'transparent-top' && mode !== 'transparent-scroll') return;

  if (header.dataset.transparencyInit === 'true') return;
  header.dataset.transparencyInit = 'true';

  if (!header.dataset.transparentState) {
    header.dataset.transparentState = 'top';
  }

  updateHeaderTransparentState(header);

  headerTransparencyScrollHandler = throttle(() => updateHeaderTransparentState(header), 16);
  window.addEventListener('scroll', headerTransparencyScrollHandler, { passive: true });
  document.addEventListener('scroll', headerTransparencyScrollHandler, { passive: true });

  const sentinel = document.querySelector('.header-transparency-sentinel');
  if (sentinel instanceof HTMLElement) {
    headerTransparencyObserver?.disconnect();
    headerTransparencyObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const nextState = entry.isIntersecting ? 'top' : 'scrolled';
        if (header.dataset.transparentState !== nextState) {
          header.dataset.transparentState = nextState;
          const headerRow = header.querySelector('[ref="headerRowTop"]');
          if (headerRow instanceof HTMLElement) {
            changeMetaThemeColor(headerRow);
          }
          updateHeaderGroupHeightsRef?.();
        }
      },
      { threshold: 0 }
    );
    headerTransparencyObserver.observe(sentinel);
  }
}

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * PERFORMANCE: Cache getBoundingClientRect result to avoid expensive DOM reads
   * @type {number | null}
   */
  #cachedTop = null;
  #cachedTopTimestamp = 0;
  #cacheTimeout = 16; // Cache for ~1 frame (16ms)

  /**
   * PERFORMANCE: Throttled scroll handler to prevent jank
   * Uses requestAnimationFrame for smooth 60fps scrolling
   */
  #handleWindowScroll = throttle(() => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    // PERFORMANCE: Cache getBoundingClientRect() result to avoid expensive DOM reads
    const now = performance.now();
    if (now - this.#cachedTopTimestamp > this.#cacheTimeout) {
      this.#cachedTop = this.getBoundingClientRect().top;
      this.#cachedTopTimestamp = now;
    }
    const headerTop = this.#cachedTop;

    if (stickyMode === 'always') {
      if (isScrollingUp) {
        if (headerTop >= 0) {
          this.dataset.scrollDirection = 'none';
        } else {
          this.dataset.scrollDirection = 'up';
        }
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (headerTop >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  }, 16); // Throttle to ~60fps (16ms)

  connectedCallback() {
    initHeaderTransparency(this);

    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentReady(() => {
  const header = document.querySelector('#header-component');
  if (header instanceof HTMLElement) {
    initHeaderTransparency(header);
  }

  const headerGroup = document.querySelector('#header-group');

  // Function to update header group height CSS variable
  const updateHeaderGroupHeight = () => {
    if (!header || !headerGroup) return;

    const headerRect = header.getBoundingClientRect();
    const headerGroupHeight = headerRect.bottom;
    const headerGroupFlowHeight = calculateHeaderGroupFlowHeight(header, headerGroup);

    document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
    document.body.style.setProperty('--header-group-flow-height', `${headerGroupFlowHeight}px`);
  };

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      updateHeaderGroupHeight();
    });

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }
    
    // Also observe the header itself for size changes
    if (header) {
      resizeObserver.observe(header);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
    
    // PERFORMANCE: Throttle scroll handler to prevent excessive updates
    // Update on scroll to catch when announcement bar hides
    let scrollTimeout = null;
    const throttledUpdate = throttle(() => {
      requestAnimationFrame(updateHeaderGroupHeight);
    }, 100); // Update max once per 100ms
    
    window.addEventListener('scroll', throttledUpdate, { passive: true });
    
    // Initial update
    updateHeaderGroupHeight();
    updateHeaderGroupHeightsRef = updateHeaderGroupHeight;
  }
});
